import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import PropTypes from 'prop-types';
import { RouteElement } from 'utils/routerCompat';
import DataManager from 'Views/DataManager/DataManager';
import ApprovalQueue from 'Views/ApprovalQueue/ApprovalQueue';
import FileImportStatus from 'Views/FileImportStatus/FileImportStatus';
import File from 'Views/File/File';
import RiskAndPerformanceRouter from 'Views/RiskAndPerformance/RiskAndPerformanceRouter';
import ManageReportingPeriods from 'Views/ManageReportingPeriods/ManageReportingPeriods';
import ManageUsers from 'Views/ManageUsers/ManageUsers';
import User from 'Views/ManageUsers/User';
import ManageRoles from 'Views/ManageRoles/ManageRoles';
import Role from 'Views/ManageRoles/Role';
import MarketDataStatus from 'Views/MarketDataStatus/MarketDataStatus';
import Output from 'Views/Output/Output';
import RPeptDashboard from 'Views/RegulatoryReportingDashboard/RPeptDashboard';
import RPeptV2Dashboard from 'Views/RegulatoryReportingDashboard/RPeptV2Dashboard';
import RPemtV3Dashboard from 'Views/RegulatoryReportingDashboard/RPemtV3Dashboard';
import RPemtV31Dashboard from 'Views/RegulatoryReportingDashboard/RPemtV31Dashboard';
import RPemtV4Dashboard from 'Views/RegulatoryReportingDashboard/RPemtV4Dashboard';
import RPceptDashboard from 'Views/RegulatoryReportingDashboard/RPceptDashboard';
import RPceptV2PerformanceDashboard from 'Views/RegulatoryReportingDashboard/RPceptV2PerformanceDashboard';
import RPceptV2HistoryDashboard from 'Views/RegulatoryReportingDashboard/RPceptV2HistoryDashboard';
import RPctiDashboard from 'Views/RegulatoryReportingDashboard/RPctiDashboard';
import ReportingOutput from 'Views/RegulatoryReportingDashboard/ReportingOutput/ReportingOutput';
import TCdcptDashboard from 'Views/RegulatoryReportingDashboard/TCdcptDashboard';
import NAVDashboard from 'Views/RegulatoryReportingDashboard/NAVDashboard';
import LoadMarketData from 'Views/LoadMarketData/LoadMarketData';
import TransactionDashboard from 'Views/TransactionDashboard/TransactionDashboard';
import PeriodComparison from 'Views/PeriodComparison/PeriodComparison';
import InsuranceProductClassDashboard from 'Views/InsuranceProductClassDashboard/InsuranceProductClassDashboard';
import ShareOverview from 'Views/Overview/ShareOverview';
import FundOverview from 'Views/Overview/FundOverview';
import ProductCalcResults from 'Views/InsuranceProduct/ProductCalcResults';
import ProductDashboard from 'Views/InsuranceProduct/ProductDashboard';
import InsProdRawCalcResults from 'Views/InsuranceProduct/InsProdRawCalcResults';
import LoadFile from 'Views/LoadFile/LoadFile';
import EPTeditor from 'Views/RegulationEditor/EPTeditor';
import EPTv2editor from 'Views/RegulationEditor/EPTv2editor';
import EMTeditor from 'Views/RegulationEditor/EMTeditor';
import EMTv3editor from 'Views/RegulationEditor/EMTv3editor';
import EMTv4editor from 'Views/RegulationEditor/EMTv4editor';
import DCPTeditor from 'Views/RegulationEditor/DCPTeditor';
import CTIeditor from 'Views/RegulationEditor/CTIeditor';
import Exports from 'Views/Exports/Exports';
import InsPreview from 'Views/InsuranceProductClassDashboard/InsPreview/InsPreview';
import FileImportStatusRouter from 'Views/FileImportStatus/FileImportStatusRouter';
import TransactionCostRouter from 'Views/TransactionCost/TransactionCostRouter';
import StaticSiteDashboard from 'Views/StaticSiteDashboard/StaticSiteDashboard';
import TransactionValidationErrors from 'Views/TransactionValidation/TransactionValidationErrors';
import TCResultsDashboard from 'Views/TransactionDashboard/TCResultsDashboard';
import TCResultsDashboardv2 from 'Views/TransactionDashboard/TCResultsDashboardv2';
import InsPublishLog from 'Views/InsPublishLog/InsPublishLog';
import Callback from 'Views/Auth0/Callback';
import { renderWithPermissions } from 'Components/Auth0/permissionFunctions';
import HomeHeader from 'Components/HomeHeader/HomeHeader';
import { HomeContentView, ReportingMessage } from './HomeContent.styles';
import OuterComponent from './OuterComponent';
import Outbound from 'Layouts/Routing/Outbound';
import ScopesList from 'Views/Distribution/ScopesList/ScopesList';
import TargetProfilesList from 'Views/Distribution/TargetProfilesList/TargetProfilesList';
import TargetProfilePreview from 'Views/Distribution/TargetProfilePreview/TargetProfilePreview';
import TargetProfilesStatus from 'Views/Distribution/TargetProfilesStatus/TargetProfilesStatus';
import Narratives from 'Views/Narratives/Narratives';
import BusinessRulesDashboard from 'Views/BusinessRulesDashboard/BusinessRulesDashboard';
import TemplateManagement from 'Views/TemplateManagement/TemplateManagement';
import ScopePage from 'Views/Distribution/ScopePage/ScopePage';
import TargetProfilePage from 'Views/Distribution/TargetProfilePage/TargetProfilePage';
import Translation from 'Views/Translation/Translation';
import ShardManagement from 'Views/ShardManagement/ShardManagement';
import OverviewV2 from 'Views/Overview/OverviewV2';

const ClientOverview = () => <div><p>Client Overview</p></div>;
const ProductOverview = () => <div><p>Product Overview</p></div>;
const FixLater = () => <div><p>Fix Later</p></div>;
const RedirectToHome = () => <Navigate to="" replace />;

/**
 * Temporal override → React.
 * When the database has a component='Outbound', this mapping redirects to the correct React component.
 * TODO: Remove when PRIIPS_metadata_route_display is updated in the DB.
 */
const blazorMigrationOverrides = {
  // Overview pages (Phase 2 & 4)
  'overview': 'OverviewV2',
  'narratives': 'Narratives',
  'business-rules': 'BusinessRulesDashboard',
  'business-rules-dashboard': 'BusinessRulesDashboard',
  'businessrules': 'BusinessRulesDashboard',
  'businessrulesdashboard': 'BusinessRulesDashboard',
  'template-management': 'TemplateManagement',
  'templatemanagement': 'TemplateManagement',
  'translation': 'Translation',
  // Distribution pages (Phase 1 & 3)
  'scopes': 'ScopesList',
  'scopes-list': 'ScopesList',
  'scopeslist': 'ScopesList',
  'target-profiles': 'TargetProfilesList',
  'profiles-list': 'TargetProfilesList',
  'profileslist': 'TargetProfilesList',
  'targetprofiles': 'TargetProfilesList',
  'profiles-status': 'TargetProfilesStatus',
  'profilesstatus': 'TargetProfilesStatus',
};

/**
 * Resolves the component to render for a given route, applying overrides for Blazor migration as needed.
 */
const resolveComponent = (componentName, fullPath) => {
  // If it's not Outbound, use the component from the database directly
  if (componentName !== 'Outbound') {
    return componentName;
  }
  
  // Extracts the last segment of the path to check for overrides (e.g., 'narratives' from '/overview/narratives')
  const pathSegments = fullPath.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1]?.toLowerCase();
  
  // Search for an override for this path
  if (lastSegment && blazorMigrationOverrides[lastSegment]) {
    console.log(`[Blazor Migration] Override: ${fullPath} → ${blazorMigrationOverrides[lastSegment]}`);
    return blazorMigrationOverrides[lastSegment];
  }
  
  // No override, keep Outbound (will redirect to Blazor)
  return componentName;
};

const compNameLookup = {
  ApprovalQueue,
  BusinessRulesDashboard,
  Callback,
  ClientOverview,
  CTIeditor,
  DataManager,
  DCPTeditor,
  EMTeditor,
  EMTv3editor,
  EMTv4editor,
  EPTeditor,
  EPTv2editor,
  Exports,
  File,
  FileImportStatus,
  FileImportStatusRouter,
  FixLater,
  FundOverview,
  InsPreview,
  InsProdRawCalcResults,
  InsPublishLog,
  InsuranceProductClassDashboard,
  LoadFile,
  LoadMarketData,
  ManageReportingPeriods,
  ManageRoles,
  ManageUsers,
  MarketDataStatus,
  NAVDashboard,
  Narratives,
  Outbound,
  Output,
  ScopesList,
  TargetProfilesList,
  TargetProfilePreview,
  TargetProfilesStatus,
  PeriodComparison,
  ProductCalcResults,
  ProductDashboard,
  ProductOverview,
  ReportingOutput,
  RiskAndPerformanceRouter,
  Role,
  RPceptDashboard,
  RPceptV2PerformanceDashboard,
  RPceptV2HistoryDashboard,
  RPctiDashboard,
  RPemtV3Dashboard,
  RPemtV31Dashboard,
  RPemtV4Dashboard,
  RPeptDashboard,
  RPeptV2Dashboard,
  ShardManagement,
  ShareOverview,
  OverviewV2,
  StaticSiteDashboard,
  TCdcptDashboard,
  TCResultsDashboard,
  TemplateManagement,
  Translation,
  ScopePage,
  TargetProfilePage,
  TCResultsDashboardv2,
  TransactionCostRouter,
  TransactionDashboard,
  TransactionValidationErrors,
  User,
};

// Components that render their own descendant <Routes> and need a trailing /* in the parent path
const routerComponents = new Set([
  'RiskAndPerformanceRouter',
  'TransactionCostRouter',
  'FileImportStatusRouter',
  'File',
]);

const HomeContent = ({
  availableRoutes = null, displayName, moduleName, selectedReportingDate = null,
}) => {
  const location = useLocation();

  // Convert absolute paths to relative paths for nested routing
  const getRelativePath = (fullPath) => {
    const prefix = `/${moduleName}`;
    if (fullPath.startsWith(prefix)) {
      const relative = fullPath.slice(prefix.length);
      return relative.startsWith('/') ? relative.slice(1) : relative;
    }
    return fullPath;
  };

  return (
    <HomeContentView>
      {
        !location.pathname.includes('/import/file')
        && !location.pathname.includes('/regulatory-reporting/preview')
        && !location.pathname.includes('/transactions/preview')
        && (
          <HomeHeader
            module={moduleName}
            moduleDisplayName={displayName}
          />
        )
      }
      <Routes>
        {
          availableRoutes && availableRoutes.map((x) => {
            const resolvedComponentName = resolveComponent(x.component, x.full_path);
            const InnerComponent = compNameLookup[resolvedComponentName];
            const relativePath = getRelativePath(x.full_path);
            const routePath = routerComponents.has(x.component) ? `${relativePath}/*` : relativePath;
            return (
              <Route
                key={x.full_path}
                path={routePath}
              element={
                x.force_reportingdate && !selectedReportingDate ? <ReportingMessage /> : (
                  <OuterComponent
                    force_reportingdate={x.force_reportingdate}
                    selectedReportingDate={
                      selectedReportingDate ? selectedReportingDate.code : null
                    }
                    routePath={x.full_path}
                  >
                    {
                    /**
                     * Checks user permissions to see if they have access to edit elements on this
                     * page.  If the user does have permissions the first function is returned and
                     * the editable property is set by what's returned from the database.  If not
                     * then the second function is run and the editable property is always set to
                     * false.
                     */
                    renderWithPermissions((canProps) => renderWithPermissions(
                      (canProps) => (
                        <RouteElement
                          Component={InnerComponent}
                          {...canProps}
                          api_name={x.api_name}
                          pk={x.pk}
                          editable={x.editable}
                          noClient={['admin'].includes(moduleName)}
                        />
                      ),
                      'edit',
                      (canProps) => (
                        <RouteElement
                          Component={InnerComponent}
                          {...canProps}
                          api_name={x.api_name}
                          pk={x.pk}
                          editable={false}
                          noClient={['admin'].includes(moduleName)}
                        />
                      ),
                    ),
                    'view')
                  }
                  </OuterComponent>
                )
              }
            />
          );
        })
      }
      <Route path="*" element={<RedirectToHome />} />
      <Route path="profile-edit" element={<TargetProfilePage />} />
      <Route path="profile-edit/:id" element={<TargetProfilePage />} />
      <Route path="profile-preview/:id" element={<TargetProfilePreview />} />
      <Route path="business-rules-dashboard" element={<BusinessRulesDashboard />} />
      <Route path="narratives" element={<Narratives />} />
      <Route path="translation" element={<Translation />} />
      <Route path="scope" element={<ScopePage />} />
      <Route path="scope/:id" element={<ScopePage />} />
    </Routes>
  </HomeContentView>
  );
};

HomeContent.propTypes = {
  availableRoutes: PropTypes.arrayOf(PropTypes.shape({
    api_name: PropTypes.shape,
    component: PropTypes.string,
    full_path: PropTypes.string,
    pk: PropTypes.string,
  })),
  displayName: PropTypes.string.isRequired,
  moduleName: PropTypes.string.isRequired,
  selectedReportingDate: PropTypes.shape({}),
};

export default React.memo(HomeContent);
